import React, { useEffect, useState } from 'react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router-dom';
import { Button, Header } from '../components';
import { FillingSpinner } from '../components/FillingSpinner';
import ClosedKiosk from '../components/ClosedKiosk';
import ErrorContainer from '../components/ErrorContainer';
import ButtonContainer from '../components/ButtonContainer';
import useUpdatingKioskState from '../hooks/useUpdatingKioskState';
import useInactivityLogout from '../hooks/useInactivityLogout';
import kioskUtils from '../utils/kiosk';
import { isMobileDevice, showLogoutButton } from '../utils';
import { CREDENTIALS, getLoginType, logout } from '../resources/auth';
import { isEmpty } from 'lodash';
import { FlexContainer, Wrapper } from '../ui/StyledComponents';

const ParkingLocation = ({ prod, disabled }) => {
  const history = useHistory();
  return <div>
    <Button
      fullwidth="true"
      className="btn-product"
      disabled={disabled}
      type="primary"
      size="large"
      style={{ fontWeight: '700' }}
      onClick={() => history.push('/kiosk', {
        parkingLocation: prod?.placeUid,
        isMultiProductKiosk: true,
      })}
    > {prod.placeName}</Button>
  </div>;
};

const ParkingLocationsSelection = ({ setKioskState }) => {
  const { t } = useTranslation();
  const [ kioskState ] = useUpdatingKioskState();
  const [ isAvailable, setIsAvailable ] = useState(false);

  useEffect(() => {
    setKioskState(kioskState);
  }, [ kioskState ]);


  const logoutActive = !isMobileDevice() && (getLoginType() === CREDENTIALS);
  useInactivityLogout(logoutActive);

  const products = kioskUtils.getVisibleProducts(kioskState.kiosk);

  // Show error if no products are shown or all the visible products are disabled
  const showNoActiveProductsError = () => {
    return products?.length === 0 ||
      products?.every(product => kioskUtils.invalidProduct(product));
  };

  useEffect(() => {
    // Check whether current time is inside opening hours
    setIsAvailable(kioskUtils.isKioskAvailable(kioskState.kiosk));
  }, [ kioskState ]);

  // Loading kiosk data
  if (!kioskState.isFetched) {
    return <FillingSpinner />;
  }

  // Closed kiosk
  if (kioskUtils.hasAvailabilityTimes(kioskState.kiosk) && !isAvailable) {
    return (
      <ErrorContainer>
        <ClosedKiosk kiosk={kioskState.kiosk}/>
      </ErrorContainer>);
  }

  // No service channel referralcodes
  if (isEmpty(kioskState.kiosk?.serviceChannelReferralCode)) {
    return (
      <ErrorContainer>
        <Alert
          type="error"
          message={t('error.noEntity', { entity: t('serviceChannelReferralCode') })} />
      </ErrorContainer>);
  }

  // No active valid products
  if ( showNoActiveProductsError()) {
    return (
      <ErrorContainer>
        <Alert
          type="error"
          message={t('error.noEntity', { entity: t('product') })} />
      </ErrorContainer>);
  }

  // Location selection disabled -> redirect
  if (!kioskState.kiosk.parkingLocationSelection) {
    return (<Redirect to={{
      pathname: '/kiosk', state: { },
    }}/>);
  }

  // remove the duplicates
  const uniquePlaceUids = new Set();
  const uniqueParkingLocations = products.filter(({ uid, placeUid }) => {
    const placeKey = placeUid || uid;
    if (uniquePlaceUids.has(placeKey)) {
      return false;
    }
    uniquePlaceUids.add(placeKey);
    return true;
  });

  // Only one product -> redirect
  if (uniqueParkingLocations
    .length === 1) {
    const product = uniqueParkingLocations[0];
    if (!kioskUtils.invalidProduct(product)) {
      return (<Redirect to={{
        pathname: '/kiosk', state: { parkingLocation: product.placeUid },
      }}/>);
    }
  }

  return (<Wrapper>
    <Header>
      {t('selectParkingLocation')}
    </Header>
    <FlexContainer>
      {uniqueParkingLocations
        .map(prod => {
          return <ParkingLocation
            key={prod.uid}
            prod={prod}
            disabled={kioskUtils.invalidProduct(prod) || !kioskState.connectionOk}/>;
        })}
    </FlexContainer>

    { kioskState.isLoading && <FillingSpinner />}
    <ButtonContainer />
    { showLogoutButton() &&
       <Button disabled={!kioskState.connectionOk} className="backButton" onClick={logout}>
         {t('logout')}
       </Button>}
  </Wrapper>);
};

export default ParkingLocationsSelection;
